import logger from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { adminLogin, fetchAdminStats, fetchPrayerTimes } from '../services/api';
import { AdminLogin, AdminHeader, AdminTabs, OverviewTab, ContactsTab, VolunteersTab, DonationsTab, EventsTab, MadrassahTab, PrayerTimesTab, SettingsTab } from '../components/admin';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminPage = () => {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState('');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Dashboard state
  const [stats, setStats] = useState(null);
  const [prayerStats, setPrayerStats] = useState(null);
  const [currentPrayer, setCurrentPrayer] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loadingData, setLoadingData] = useState(false);
  
  // Tab-specific data
  const [contacts, setContacts] = useState([]);
  const [volunteerDatabase, setVolunteerDatabase] = useState([]);
  const [volunteerStats, setVolunteerStats] = useState(null);
  const [donations, setDonations] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventStats, setEventStats] = useState(null);
  const [madrassahApplications, setMadrassahApplications] = useState([]);
  const [madrassahStudents, setMadrassahStudents] = useState([]);
  const [madrassahVolunteers, setMadrassahVolunteers] = useState([]);
  const [madrassahStudentStats, setMadrassahStudentStats] = useState(null);
  const [madrassahVolunteerStats, setMadrassahVolunteerStats] = useState(null);

  // Auth effects
  useEffect(() => {
    const savedToken = sessionStorage.getItem('fatimia_admin_token');
    if (savedToken) { setToken(savedToken); setIsLoggedIn(true); }
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) loadDashboardData();
  }, [isLoggedIn, token]); // loadDashboardData is stable - defined in component scope

  // Data loaders
  const loadDashboardData = async () => {
    try {
      const [statsData, prayerData, prayerStatsData] = await Promise.all([
        fetchAdminStats(token), fetchPrayerTimes(),
        fetch(`${API_BASE_URL}/api/prayer-times/stats`).then(r => r.json())
      ]);
      setStats(statsData); setCurrentPrayer(prayerData); setPrayerStats(prayerStatsData);
    } catch (err) { logger.error('Failed to load dashboard:', err); }
  };

  const loadSubmissions = async (type) => {
    setLoadingData(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/${type}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      switch(type) {
        case 'contacts': setContacts(data); break;
        case 'volunteer-database': setVolunteerDatabase(data); break;
        case 'donations': setDonations(data); break;
        case 'madrassah': setMadrassahApplications(data); break;
        case 'madrassah-students': setMadrassahStudents(data); break;
        case 'madrassah-volunteers': setMadrassahVolunteers(data); break;
        case 'events': setEvents(data); break;
        default: break;
      }
    } catch (err) { logger.error(`Failed to load ${type}:`, err); }
    finally { setLoadingData(false); }
  };

  const loadVolunteerStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/volunteer-database/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setVolunteerStats(await response.json());
    } catch (err) { logger.error('Failed to load volunteer stats:', err); }
  };

  const loadMadrassahStats = async () => {
    try {
      const [studentStats, volStats] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/madrassah-students/stats`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API_BASE_URL}/api/admin/madrassah-volunteers/stats`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json())
      ]);
      setMadrassahStudentStats(studentStats);
      setMadrassahVolunteerStats(volStats);
    } catch (err) { logger.error('Failed to load madrassah stats:', err); }
  };

  const loadEventStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/events/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setEventStats(await response.json());
    } catch (err) { logger.error('Failed to load event stats:', err); }
  };

  // Tab change handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'contacts') loadSubmissions('contacts');
    else if (tab === 'volunteer-db') { loadSubmissions('volunteer-database'); loadVolunteerStats(); }
    else if (tab === 'donations') loadSubmissions('donations');
    else if (tab === 'madrassah') { loadSubmissions('madrassah'); loadSubmissions('madrassah-students'); loadSubmissions('madrassah-volunteers'); loadMadrassahStats(); }
    else if (tab === 'events') { loadSubmissions('events'); loadEventStats(); }
    // PrayerTimesTab handles its own data loading internally
  };

  // Auth handlers
  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setLoginError('');
    try {
      const result = await adminLogin(loginData);
      setToken(result.access_token); sessionStorage.setItem('fatimia_admin_token', result.access_token); setIsLoggedIn(true);
    } catch (err) { setLoginError('Invalid username or password'); }
    finally { setLoading(false); }
  };

  const handleLogout = () => { setIsLoggedIn(false); setToken(''); sessionStorage.removeItem('fatimia_admin_token'); setStats(null); };

  if (!isLoggedIn) {
    return <AdminLogin loginData={loginData} setLoginData={setLoginData} loginError={loginError} loading={loading} onSubmit={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-[#f8f6f1]">
      <AdminHeader onLogout={handleLogout} />
      <AdminTabs activeTab={activeTab} onTabChange={handleTabChange} stats={stats} />
      
      <div className="container mx-auto px-4 py-8">
        {activeTab === 'overview' && <OverviewTab stats={stats} onTabChange={handleTabChange} />}

        {activeTab === 'events' && (
          <EventsTab
            events={events}
            eventStats={eventStats}
            loadingData={loadingData}
            token={token}
            apiBaseUrl={API_BASE_URL}
            onRefresh={() => loadSubmissions('events')}
            onStatsRefresh={loadEventStats}
            onDashboardRefresh={loadDashboardData}
          />
        )}

        {activeTab === 'contacts' && (
          <ContactsTab contacts={contacts} loadingData={loadingData} token={token} onRefresh={() => loadSubmissions('contacts')} onDashboardRefresh={loadDashboardData} apiBaseUrl={API_BASE_URL} />
        )}

        {activeTab === 'volunteer-db' && (
          <VolunteersTab volunteerDatabase={volunteerDatabase} volunteerStats={volunteerStats} loadingData={loadingData} token={token} onRefresh={() => loadSubmissions('volunteer-database')} onStatsRefresh={loadVolunteerStats} onDashboardRefresh={loadDashboardData} apiBaseUrl={API_BASE_URL} />
        )}

        {activeTab === 'madrassah' && (
          <MadrassahTab
            madrassahApplications={madrassahApplications}
            madrassahStudents={madrassahStudents}
            madrassahVolunteers={madrassahVolunteers}
            madrassahStudentStats={madrassahStudentStats}
            madrassahVolunteerStats={madrassahVolunteerStats}
            stats={stats}
            loadingData={loadingData}
            token={token}
            apiBaseUrl={API_BASE_URL}
            onRefreshApplications={() => loadSubmissions('madrassah')}
            onRefreshStudents={() => loadSubmissions('madrassah-students')}
            onRefreshVolunteers={() => loadSubmissions('madrassah-volunteers')}
            onStatsRefresh={loadMadrassahStats}
            onDashboardRefresh={loadDashboardData}
          />
        )}

        {activeTab === 'donations' && (
          <DonationsTab donations={donations} loadingData={loadingData} token={token} onRefresh={() => loadSubmissions('donations')} onDashboardRefresh={loadDashboardData} apiBaseUrl={API_BASE_URL} />
        )}

        {activeTab === 'prayer' && (
          <PrayerTimesTab
            token={token}
            apiBaseUrl={API_BASE_URL}
            currentPrayer={currentPrayer}
            prayerStats={prayerStats}
            onDashboardRefresh={loadDashboardData}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab token={token} apiBaseUrl={API_BASE_URL} />
        )}
      </div>
    </div>
  );
};

export default AdminPage;
